// Admin Schedule Page — Manage availability (Firestore-backed)
import { renderHeader, renderBottomNav } from '../components/header.js';
import { toastSuccess } from '../components/toast.js';
import { requireAdmin } from '../auth.js';
import { formatThaiDate } from '../components/calendar.js';
import { getAllFortuneTellers, getAllSchedulesByFT, saveSchedulesBatch, toggleScheduleActive, updateScheduleSlotsDB } from '../services/db.js';

const DEFAULT_SLOTS = ['10:00', '11:00', '12:00', '13:00', '14:00'];

export async function renderAdminSchedule(retainScroll = false) {
  const allowed = await requireAdmin();
  if (!allowed) return;

  const app = document.getElementById('app');
  const currentScroll = window.scrollY;

  if (!retainScroll) {
    app.innerHTML = `${renderHeader()}<div class="page"><div style="text-align:center;padding:var(--space-xl);"><div class="loading-spinner"></div></div></div>${renderBottomNav('admin-schedule')}`;
  }

  const tellers = await getAllFortuneTellers();

  if (tellers.length === 0) {
    app.innerHTML = `
      ${renderHeader()}
      <div class="page">
        <h2 class="page-title">📅 จัดการตารางเวลา</h2>
        <div class="empty-state">
          <div class="empty-state-icon">🔮</div>
          <div class="empty-state-title">ยังไม่มีข้อมูลหมอดู</div>
          <div class="empty-state-text">กรุณาเพิ่มข้อมูลหมอดูก่อน</div>
          <button class="btn btn-primary mt-lg" onclick="location.hash='#/admin/fortune-tellers'">🔮 จัดการหมอดู</button>
        </div>
      </div>
      ${renderBottomNav('admin-schedule')}
    `;
    return;
  }

  // Use first fortune teller for now (or could add selector)
  const selectedFt = tellers[0];
  
  // Filter out past schedules (keep only >= today)
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  const localTodayStr = new Date(today.getTime() - tzOffset).toISOString().split('T')[0];
  
  let schedules = await getAllSchedulesByFT(selectedFt.id);
  schedules = schedules.filter(s => s.date >= localTodayStr);

  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <h2 class="page-title">📅 จัดการตารางเวลา</h2>

      ${tellers.length > 1 ? `
      <div class="form-group mb-lg">
        <label class="form-label">เลือกหมอดู</label>
        <select class="form-select" id="ft-selector">
          ${tellers.map(ft => `<option value="${ft.id}" ${ft.id === selectedFt.id ? 'selected' : ''}>${ft.name}</option>`).join('')}
        </select>
      </div>
      ` : `<p class="text-sm text-muted mb-lg">🔮 ${selectedFt.name}</p>`}

      <!-- Quick Add Schedule -->
      <div class="card mb-lg">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:var(--space-md); flex-wrap:wrap; gap:8px;">
          <h3 style="color:var(--accent-gold); margin:0;">➕ สร้างตารางเวลา (รวดเดียวหลายวัน)</h3>
          <div style="background:var(--bg-glass); padding:4px 12px; border-radius:20px; font-size:var(--font-xs); border:1px solid var(--border-color); display:flex; gap:6px; align-items:center;">
            <span style="opacity:0.8;">📍 ปัจจุบัน:</span>
            <strong id="schedule-today-display" style="color:var(--accent-gold);">กำลังโหลด...</strong>
          </div>
        </div>
        
        <div style="display:flex; gap:var(--space-sm); margin-bottom:var(--space-md); flex-wrap:wrap;">
          <button class="btn btn-sm btn-outline quick-date-btn" data-type="this-week">⚡ สัปดาห์นี้</button>
          <button class="btn btn-sm btn-outline quick-date-btn" data-type="next-week">⚡ สัปดาห์หน้า</button>
          <button class="btn btn-sm btn-outline quick-date-btn" data-type="this-month">⚡ เดือนนี้</button>
          <button class="btn btn-sm btn-outline quick-date-btn" data-type="next-month">⚡ เดือนหน้า</button>
        </div>

        <div style="display:flex; gap:var(--space-md); flex-wrap:wrap;">
          <div class="form-group" style="flex:1; min-width: 130px;">
            <label class="form-label">เริ่มวันที่</label>
            <input class="form-input" type="date" id="start-date" min="${new Date().toISOString().split('T')[0]}" />
          </div>
          <div class="form-group" style="flex:1; min-width: 130px;">
            <label class="form-label">ถึงวันที่</label>
            <input class="form-input" type="date" id="end-date" min="${new Date().toISOString().split('T')[0]}" />
          </div>
        </div>
        
        <label style="display:flex;align-items:center;gap:8px;margin-bottom:var(--space-md);cursor:pointer;font-size:var(--font-sm);">
          <input type="checkbox" id="exclude-weekends" checked />
          <span>ยกเว้น วันเสาร์ - อาทิตย์</span>
        </label>

        <div class="form-group">
          <label class="form-label">เวลาที่เปิดรับ (จะถูกจำลองไปทุกวันด้านบน)</label>
          <div style="display:flex; flex-wrap:wrap; gap:var(--space-xs);" id="slot-checkboxes">
            ${DEFAULT_SLOTS.map(s => `
              <label style="display:flex;align-items:center;gap:4px;background:var(--bg-glass);padding:6px 12px;border-radius:8px;cursor:pointer;font-size:var(--font-sm);">
                <input type="checkbox" value="${s}" class="slot-check" checked /> ${s}
              </label>
            `).join('')}
          </div>
        </div>
        <button class="btn btn-primary btn-block" id="add-schedule-btn">📅 บันทึกเวลาที่เลือก (หลายวัน)</button>
      </div>

      <!-- Existing Schedules -->
      <h3 class="mb-md" style="color:var(--accent-gold);">📋 ตารางวันรับจอง</h3>
      <div style="display:flex; gap:8px; margin-bottom:var(--space-md); font-size:var(--font-xs);">
        <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;background:#06C755;border-radius:50%;display:inline-block;"></span> เปิดรับจอง</span>
        <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;background:#e94d4d;border-radius:50%;display:inline-block;"></span> ปิดรับจอง</span>
      </div>
      <div id="schedule-list">
        ${schedules.length > 0 ? schedules.sort((a, b) => a.date.localeCompare(b.date)).map(s => renderScheduleDay(s)).join('') : `
          <div class="empty-state">
            <div class="empty-state-icon">📅</div>
            <div class="empty-state-text">ยังไม่มีตารางเวลา เพิ่มวันว่างด้านบน</div>
          </div>
        `}
      </div>
    </div>
    
    <!-- Edit Slots Modal -->
    <div id="edit-slots-modal" class="modal" style="display:none; position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); z-index:1000; align-items:center; justify-content:center;">
      <div class="card" style="width:90%; max-width:400px; background:var(--surface);">
        <div class="card-body">
          <h3 style="color:var(--accent-gold); margin-bottom:var(--space-md);">✏️ แก้ไขเวลา <span id="edit-modal-date"></span></h3>
          <div style="display:flex; flex-wrap:wrap; gap:var(--space-xs); margin-bottom:var(--space-md);" id="edit-modal-checkboxes">
          </div>
          <div style="display:flex; gap:var(--space-sm);">
            <button class="btn btn-outline btn-block" id="close-edit-modal">ยกเลิก</button>
            <button class="btn btn-primary btn-block" id="save-edit-modal">💾 บันทึกเวลา</button>
          </div>
        </div>
      </div>
    </div>

    ${renderBottomNav('admin-schedule')}
  `;

  if (retainScroll) {
    setTimeout(() => window.scrollTo(0, currentScroll), 10);
  }

  // Start local clock
  if (window.scheduleClockInt) clearInterval(window.scheduleClockInt);
  window.scheduleClockInt = setInterval(() => {
    const el = document.getElementById('schedule-today-display');
    if (!el) {
       clearInterval(window.scheduleClockInt);
       return;
    }
    const dOpts = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Bangkok' };
    const tOpts = { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Bangkok', hour12: false };
    const now = new Date();
    el.innerHTML = now.toLocaleDateString('th-TH', dOpts) + ' • ' + now.toLocaleTimeString('th-TH', tOpts) + ' น.';
  }, 1000);

  // Quick Date Helpers
  document.querySelectorAll('.quick-date-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const type = btn.dataset.type;
      const today = new Date();
      let start, end;
      
      const setDates = (s, e) => {
        // Fix local timezone offset issue for simple yyyy-mm-dd format
        const fmt = (d) => {
          const tzOffset = d.getTimezoneOffset() * 60000;
          return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
        };
        document.getElementById('start-date').value = fmt(s);
        document.getElementById('end-date').value = fmt(e);
      };

      if (type === 'this-week') {
        start = new Date();
        end = new Date();
        const diff = 7 - end.getDay(); // assumes week ends on Sunday
        end.setDate(end.getDate() + (diff === 7 ? 0 : diff));
        setDates(start, end);
      } else if (type === 'next-week') {
        start = new Date();
        start.setDate(start.getDate() + (7 - start.getDay()) + 1); // Next Monday
        end = new Date(start);
        end.setDate(end.getDate() + 6); // Next Sunday
        setDates(start, end);
      } else if (type === 'this-month') {
        start = new Date();
        end = new Date(start.getFullYear(), start.getMonth() + 1, 0); // Last day of month
        setDates(start, end);
      } else if (type === 'next-month') {
        start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        setDates(start, end);
      }
    });
  });

  // Batch Save Logic
  document.getElementById('add-schedule-btn')?.addEventListener('click', async () => {
    const startStr = document.getElementById('start-date')?.value;
    const endStr = document.getElementById('end-date')?.value;
    const excludeWeekends = document.getElementById('exclude-weekends')?.checked;
    
    if (!startStr) { toastSuccess('กรุณาเลือกวันที่เริ่มต้น'); return; }
    
    // If no end date, just use start date
    const actualEndStr = endStr || startStr;
    const startDate = new Date(startStr);
    const endDate = new Date(actualEndStr);
    
    if (endDate < startDate) { toastSuccess('วันที่สิ้นสุดต้องมากกว่าหรือเท่ากับวันเริ่มต้น'); return; }

    const slots = Array.from(document.querySelectorAll('.slot-check:checked')).map(c => c.value);
    if (slots.length === 0) { toastSuccess('กรุณาเลือกเวลาอย่างน้อย 1 ช่วง'); return; }

    const btn = document.getElementById('add-schedule-btn');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังสร้างตาราง...';

    // Generate valid dates
    const datesToSave = [];
    let current = new Date(startDate);
    while (current <= endDate) {
        const dayOfWeek = current.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sun, 6 = Sat
        
        if (!(excludeWeekends && isWeekend)) {
            const offsetDate = new Date(current.getTime() - (current.getTimezoneOffset() * 60000));
            datesToSave.push(offsetDate.toISOString().split('T')[0]);
        }
        current.setDate(current.getDate() + 1);
    }
    
    if (datesToSave.length === 0) {
        toastSuccess('ไม่มีวันที่ถูกเลือก (อาจตรงกับวันหยุดเสาร์-อาทิตย์ทั้งหมด)');
        btn.disabled = false;
        btn.textContent = '📅 สร้างตารางเวลาทีละหลายๆวัน';
        return;
    }

    await saveSchedulesBatch(selectedFt.id, datesToSave, slots);
    toastSuccess(`สร้างคิวสำเร็จทั้งหมด ${datesToSave.length} วัน ✅`);
    renderAdminSchedule(true);
  });

  // Toggle schedule active/inactive
  document.querySelectorAll('.toggle-schedule').forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = '⏳...';
      const id = btn.dataset.id;
      const active = btn.dataset.active === 'true';
      await toggleScheduleActive(id, !active);
      toastSuccess(!active ? 'เปิดรับวันนี้' : 'ปิดรับวันนี้');
      renderAdminSchedule(true);
    });
  });

  // Edit schedule slots logic
  let editingScheduleId = null;
  document.querySelectorAll('.edit-schedule-slots').forEach(btn => {
    btn.addEventListener('click', () => {
      editingScheduleId = btn.dataset.id;
      const dateStr = btn.dataset.date;
      const currentSlots = JSON.parse(btn.dataset.slots || '[]');
      
      document.getElementById('edit-modal-date').textContent = formatThaiDate(dateStr);
      
      const checkboxesHtml = DEFAULT_SLOTS.map(s => `
        <label style="display:flex;align-items:center;gap:4px;background:var(--bg-glass);padding:6px 12px;border-radius:8px;cursor:pointer;font-size:var(--font-sm);">
          <input type="checkbox" value="${s}" class="edit-slot-check" ${currentSlots.includes(s) ? 'checked' : ''} /> ${s}
        </label>
      `).join('');
      
      document.getElementById('edit-modal-checkboxes').innerHTML = checkboxesHtml;
      document.getElementById('edit-slots-modal').style.display = 'flex';
    });
  });

  document.getElementById('close-edit-modal')?.addEventListener('click', () => {
    document.getElementById('edit-slots-modal').style.display = 'none';
    editingScheduleId = null;
  });

  document.getElementById('save-edit-modal')?.addEventListener('click', async () => {
    if (!editingScheduleId) return;
    const slots = Array.from(document.querySelectorAll('.edit-slot-check:checked')).map(c => c.value);
    
    if (slots.length === 0) {
      toastSuccess('กรุณาเลือกเวลาอย่างน้อย 1 ช่วง');
      return;
    }

    const btn = document.getElementById('save-edit-modal');
    btn.disabled = true;
    btn.textContent = '⏳ กำลังบันทึก...';

    await updateScheduleSlotsDB(editingScheduleId, slots);
    toastSuccess('บันทึกเวลาสำเร็จ ✅');
    
    document.getElementById('edit-slots-modal').style.display = 'none';
    renderAdminSchedule(true);
  });

  // Fortune teller selector
  document.getElementById('ft-selector')?.addEventListener('change', () => {
    renderAdminSchedule();
  });
}

function renderScheduleDay(schedule) {
  const bookedCount = (schedule.booked_slots || []).length;
  const totalSlots = (schedule.slots || []).length;
  const availableSlots = totalSlots - bookedCount;
  const isActive = schedule.active !== false;

  return `
    <div class="card" style="margin-bottom:var(--space-sm); ${!isActive ? 'opacity:0.65; border-left:3px solid #e94d4d;' : 'border-left:3px solid #06C755;'}">
      <div class="flex-between">
        <div>
          <p class="fw-600" style="${!isActive ? 'color:var(--text-secondary);' : ''}">
            ${!isActive ? '🔒 ' : ''}${formatThaiDate(schedule.date)}
          </p>
          <p class="text-xs text-muted">
            ว่าง ${availableSlots}/${totalSlots} ช่วงเวลา
            ${bookedCount > 0 ? ` · จอง ${bookedCount} คิว` : ''}
            ${!isActive ? ' · <span style="color:#e94d4d;font-weight:600;">ปิดรับจอง</span>' : ''}
          </p>
          <p class="text-xs text-muted mt-xs">${(schedule.slots || []).join(', ')}</p>
        </div>
        <div style="display:flex; flex-direction:column; gap:8px;">
          <button class="btn btn-sm btn-outline edit-schedule-slots"
                  data-id="${schedule.id}" data-date="${schedule.date}" data-slots='${JSON.stringify(schedule.slots || [])}'
                  style="min-width:72px;">
            ✏️ แก้ไข
          </button>
          <button class="btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline'} toggle-schedule"
                  data-id="${schedule.id}" data-active="${isActive}"
                  style="${!isActive ? 'border-color:#e94d4d;color:#e94d4d;' : ''} min-width:72px;">
            ${isActive ? '✅ เปิด' : '❌ ปิด'}
          </button>
        </div>
      </div>
    </div>
  `;
}
